/* src/locations/cards/AddLocationCard/index.js */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import _ from 'lodash';
import ReactMapboxGl from 'react-mapbox-gl';
import { loadTexts } from '../../../../config/texts';

import { withStyles } from '@material-ui/core/styles';
import {
  AppBar,
  Button,
  Card,
  Divider,
  FormControl,
  InputLabel,
  Modal,
  MenuItem,
  Select,
  TextField,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { Add, Save } from '@material-ui/icons';
import Geocoder from './Geocoder';

import {
  locationNameChanged,
  locationAddressChanged,
  locationCityChanged,
  locationTypeChanged,
} from './actions';
import {
  addLocationToUniversity,
  addInterviewLocation,
  editLocation,
} from '../../actions';
import { mapboxKey } from '../../../../config/settings';
import styles from './styles';
const TEXT_KEY = "location";

const fields = ['name', 'address', 'city', 'type'];
const categories = ['cafe', 'university', 'interview', 'online_interview'];

const Map = ReactMapboxGl({ accessToken: mapboxKey });

class AddLocationCard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: props.open,
      mode: props.mode,
      location: props.location,
      texts: loadTexts(TEXT_KEY, props.locale)
    };

    this._handleCategory = this._handleCategory.bind(this);
    this._onChangeText = this._onChangeText.bind(this);
    this._onSubmit = this._onSubmit.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    const { open, mode, location } = nextProps;
    this.setState({ open, mode, location });
  }

  _handleCategory(event) {
    this.props.locationTypeChanged(event.target.value);
  }

  _onChangeText(field) {
    return event => {
      this.props[`location${_.upperFirst(field)}Changed`](event.target.value);
    };
  }

  async _onSubmit() {
    const { mode, location } = this.state;
    const {
      name,
      address,
      city,
      type,
      longitude,
      latitude,
      universityID,
      organizationID,
      from,
    } = this.props;
    if (mode === 'add') {
      let addFunc;
      const params = { locationInput: {
        name,
        address,
        city,
        type: type.toUpperCase(),
        coordinates: { longitude, latitude },
      } };

      if (from === 'interview') {
        addFunc = this.props.addInterviewLocation;
        params.organization = organizationID;
      } else {
        addFunc = this.props.addLocationToUniversity;
        params.university = universityID;
      }

      await addFunc(params);
    } else {
      await this.props.editLocation({
        location, // get lesson id... somehow
        updateLocationInput: {
          name,
          address,
          city,
          coordinates: { longitude, latitude },
        },
      });
    }
    this.props.onSubmit();
  }

  renderTextFields(classes) {
    const { texts } = this.state;
    return (
      <div className={classes.textFieldContainer}>
        {fields.map(f => {
          if (f === 'type') {
            return (
              <FormControl key={f} className={classes.formControl}>
                <InputLabel htmlFor="category">{texts.label.category}</InputLabel>
                <Select
                  value={this.props.type}
                  onChange={this._handleCategory}
                  inputProps={{ name: 'category', id: 'category' }}
                  disabled={this.props.disabled}
                >
                  {_.difference(categories, this.props.removedCategories).map(c =>
                    <MenuItem key={c} value={c}>{_.upperFirst(c)}</MenuItem>)}
                </Select>
              </FormControl>
            );
          }
          return (
            <TextField
              id={f}
              key={f}
              label={_.upperFirst(f)}
              className={classNames(classes.textField, classes[`textField-${f}`])}
              margin="normal"
              value={this.props[f]}
              onChange={this._onChangeText(f)}
              fullWidth
            />
          );
        })}
      </div>
    );
  }

  render() {
    const { onClose, classes, longitude, latitude } = this.props;
    const { open, mode, texts } = this.state;

    const centerParams = {};
    if (longitude && latitude) centerParams.center = [longitude, latitude];

    const disabled = fields.map(f => this.props[f]).filter(Boolean).length !== fields.length;

    return (
      <Modal
        aria-labelledby={texts.modal.title}
        aria-describedby={texts.modal.subtitle}
        open={open}
        onClose={() => onClose('edit')}
      >
        <Card className={classes.card}>
          <AppBar position="static">
            <Toolbar>
              <Typography variant="title" color="inherit">
                {_.upperFirst(mode)} a Location
              </Typography>
            </Toolbar>
          </AppBar>
          <Typography color="secondary" className={classes.message}>
            {texts.modal.format_address}
          </Typography>
          <div className={classes.content}>
            <Map
              // eslint-disable-next-line
              style="mapbox://styles/nimbus-dev/cjjyo6qf8494r2rqrots0hdwq"
              containerStyle={{ height: '300px', width: '600px' }}
              {...centerParams}
            >
              <Geocoder {...centerParams} />
            </Map>
            {this.renderTextFields(classes)}
          </div>
          <Divider />
          <div className={classes.buttonContainer}>
            <Button
              color="primary"
              variant="raised"
              className={classes.button}
              onClick={this._onSubmit}
              disabled={disabled}
            >
              {mode === 'add' && (
                <React.Fragment>
                  <Add className={classes.icon} />
                  {texts.button_text.add_location}
                </React.Fragment>
              )}
              {mode === 'edit' && (
                <React.Fragment>
                  <Save className={classes.icon} />
                  {texts.button_text.save}
                </React.Fragment>
              )}
            </Button>
          </div>
        </Card>
      </Modal>
    );
  }
}

AddLocationCard.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  disabled: PropTypes.bool,
  removedCategories: PropTypes.arrayOf(PropTypes.string),
  onSubmit: PropTypes.func,
  universityID: PropTypes.string.isRequired,
  organizationID: PropTypes.string.isRequired,
  from: PropTypes.string,
};

AddLocationCard.defaultProps = {
  disabled: false,
  removedCategories: [],
  onSubmit: () => {},
  from: '',
};

const mapStateToProps = ({ locations: { input }, auth, appState }) => ({
  name: input.name,
  address: input.address,
  city: input.city,
  type: input.type,
  longitude: input.longitude,
  latitude: input.latitude,
  universityID: _.get(auth, 'selectedUniversity') || '',
  organizationID: _.get(auth, 'selectedOrganization') || '',
  locale: appState.locale,
});

const mapDispatchToProps = dispatch => ({
  locationNameChanged: text => dispatch(locationNameChanged(text)),
  locationAddressChanged: text => dispatch(locationAddressChanged(text)),
  locationCityChanged: text => dispatch(locationCityChanged(text)),
  locationTypeChanged: text => dispatch(locationTypeChanged(text)),
  addLocationToUniversity: input => dispatch(addLocationToUniversity(input)()),
  editLocation: params => dispatch(editLocation(params)()),
  addInterviewLocation: params => dispatch(addInterviewLocation(params)()),
});

export default compose(
  withStyles(styles, { name: 'AddLocationCard' }),
  connect(mapStateToProps, mapDispatchToProps)
)(AddLocationCard);



// WEBPACK FOOTER //
// ./src/modules/locations/cards/AddLocationCard/index.js